import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { 
  User, Building2, FileText, CheckCircle, ArrowRight, ArrowLeft, 
  Phone, Mail, Hash, Package, Truck, ChevronDown, ChevronUp,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { safeFetchCompat as fetch } from '../utils/safeFetch';

// Submission progress steps
const SUBMISSION_STEPS = [
  { id: 'validating', label: 'Validating your details...', icon: '🔍' },
  { id: 'saving', label: 'Saving your registration...', icon: '💾' },
  { id: 'notifications', label: 'Sending confirmations...', icon: '📧' },
  { id: 'complete', label: 'Almost done!', icon: '✨' },
];

const SubscriptionSignupPage = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submissionStep, setSubmissionStep] = useState(0); // Track submission progress
  
  // Form data
  const [formData, setFormData] = useState({
    // Key Contact
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    // Company Details
    businessName: '',
    abn: '',
    // Accounts Contact
    accountsSameAsKey: false,
    accountsFirstName: '',
    accountsLastName: '',
    accountsEmail: '',
    accountsPhone: '',
    // Services
    interestedInCollection: false,
    interestedInDelivery: false,
    // Legal
    agreedTerms: false,
  });

  // Track if user has scrolled to bottom of terms
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [showScrollReminder, setShowScrollReminder] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Handle scroll in terms section
  const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Check if scrolled to bottom (with 20px tolerance)
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasReadTerms(true);
    }
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!formData.firstName.trim()) return 'First name is required';
        if (!formData.lastName.trim()) return 'Last name is required';
        if (!formData.email.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
        if (!formData.mobile.trim()) return 'Mobile number is required';
        if (!/^04\d{8}$/.test(formData.mobile.replace(/\s/g, ''))) return 'Please enter a valid Australian mobile (04XX XXX XXX)';
        return null;
      case 2:
        if (!formData.businessName.trim()) return 'Business name is required';
        if (!formData.abn.trim()) return 'ABN is required';
        if (!/^\d{11}$/.test(formData.abn.replace(/\s/g, ''))) return 'ABN must be 11 digits';
        if (!formData.accountsSameAsKey) {
          if (!formData.accountsFirstName.trim()) return 'Accounts contact first name is required';
          if (!formData.accountsLastName.trim()) return 'Accounts contact last name is required';
          if (!formData.accountsEmail.trim()) return 'Accounts contact email is required';
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.accountsEmail)) return 'Please enter a valid accounts email';
        }
        return null;
      case 3:
        if (!formData.interestedInCollection && !formData.interestedInDelivery) {
          return 'Please select at least one service';
        }
        return null;
      case 4:
        if (!formData.agreedTerms) return 'Please check the box to accept the terms';
        return null;
      default:
        return null;
    }
  };

  const nextStep = () => {
    const validationError = validateStep(step);
    if (validationError) {
      setError(validationError);
      return;
    }
    setStep(prev => prev + 1);
    setError('');
  };

  const handleSubmitClick = () => {
    if (!hasReadTerms) {
      setShowScrollReminder(true);
      setTimeout(() => setShowScrollReminder(false), 3000);
      return;
    }
    handleSubmit();
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = async () => {
    const validationError = validateStep(4);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSubmissionStep(0);

    try {
      // Step 1: Validating
      await new Promise(resolve => setTimeout(resolve, 600));
      setSubmissionStep(1);

      const payload = {
        key_contact: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          mobile: formData.mobile.replace(/\s/g, ''),
        },
        company: {
          business_name: formData.businessName,
          abn: formData.abn.replace(/\s/g, ''),
        },
        accounts_contact: formData.accountsSameAsKey ? {
          same_as_key_contact: true,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.mobile.replace(/\s/g, ''),
        } : {
          same_as_key_contact: false,
          first_name: formData.accountsFirstName,
          last_name: formData.accountsLastName,
          email: formData.accountsEmail,
          phone: formData.accountsPhone.replace(/\s/g, ''),
        },
        services_interested: {
          packaging_collection: formData.interestedInCollection,
          delivery: formData.interestedInDelivery,
        },
        legal_agreements: {
          general_terms: formData.agreedTerms,
          service_terms: formData.agreedTerms,
          privacy_policy: true,
          agreed_at: new Date().toISOString(),
        },
      };

      // Step 2: Saving
      const response = await fetch(`${API_URL}/api/subscription-signups/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to submit registration';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, try to get text
          try {
            const textError = await response.text();
            if (textError) errorMessage = textError;
          } catch (e) {
            // Use status text as fallback
            errorMessage = `Registration failed (${response.status}: ${response.statusText})`;
          }
        }
        throw new Error(errorMessage);
      }

      // Step 3: Notifications sent (backend sends emails)
      setSubmissionStep(2);
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 4: Complete
      setSubmissionStep(3);
      await new Promise(resolve => setTimeout(resolve, 500));

      setIsSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      setSubmissionStep(0);
    }
  };

  // Progress indicator
  const ProgressIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((num) => (
        <React.Fragment key={num}>
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              step >= num 
                ? 'bg-[#0D9488] text-white' 
                : 'bg-[#E2E8F0] text-[#64748B]'
            }`}
          >
            {step > num ? <CheckCircle className="w-5 h-5" /> : num}
          </div>
          {num < 4 && (
            <div className={`w-12 sm:w-20 h-1 mx-1 ${step > num ? 'bg-[#0D9488]' : 'bg-[#E2E8F0]'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step labels
  const stepLabels = ['Contact', 'Company', 'Services', 'Legal'];

  // Thank You Screen
  if (isSubmitted) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-white py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="border-[#E2E8F0] shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#059669]" />
              </div>
              <h1 className="text-3xl font-bold text-[#111111] mb-4">
                Thank You for Your Interest!
              </h1>
              <p className="text-lg text-[#475569] mb-6">
                We've received your subscription registration request for <strong>{formData.businessName}</strong>.
              </p>
              <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-6 mb-8 text-left">
                <h3 className="font-semibold text-[#166534] mb-3">What happens next?</h3>
                <ul className="space-y-2 text-[#166534]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>Our team will review your application</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>We'll be in touch within 1-2 business days</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>We'll discuss your needs and set up your account</span>
                  </li>
                </ul>
              </div>
              <p className="text-[#64748B] mb-8">
                Questions? Contact us at{' '}
                <a href="mailto:hello@transa.com.au" className="text-[#0D9488] hover:underline">
                  hello@transa.com.au
                </a>
              </p>
              <Link to="/">
                <Button className="bg-[#0D9488] hover:bg-[#0F766E] text-white px-8">
                  Back to Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-white py-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#111111] mb-3">
            Become a Subscription Client
          </h1>
          <p className="text-lg text-[#475569]">
            Join installers across Melbourne who've simplified their logistics with Transa
          </p>
        </div>

        {/* Progress */}
        <ProgressIndicator />
        
        {/* Step Labels */}
        <div className="flex justify-between mb-8 px-2">
          {stepLabels.map((label, idx) => (
            <span 
              key={label} 
              className={`text-sm font-medium ${step === idx + 1 ? 'text-[#0D9488]' : 'text-[#94A3B8]'}`}
            >
              {label}
            </span>
          ))}
        </div>

        <Card className="border-[#E2E8F0] shadow-lg">
          <CardContent className="p-6 sm:p-8">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-4 rounded-lg mb-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                    <span className="text-red-500 text-sm">!</span>
                  </div>
                  <div>
                    <p className="font-medium">{error}</p>
                    {error.includes('already') && (
                      <p className="text-sm mt-2 text-red-600">
                        If you believe this is an error, please contact us at{' '}
                        <a href="mailto:hello@transa.com.au" className="underline">hello@transa.com.au</a>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Key Contact Details */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#F0FDFA] flex items-center justify-center">
                    <User className="w-6 h-6 text-[#0D9488]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#111111]">Key Contact Details</h2>
                    <p className="text-[#64748B]">Primary contact for your subscription account</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">First Name *</label>
                    <Input
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      className="h-12"
                      data-testid="key-contact-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">Last Name *</label>
                    <Input
                      placeholder="Smith"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      className="h-12"
                      data-testid="key-contact-last-name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-2">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <Input
                      type="email"
                      placeholder="john@company.com.au"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="h-12 pl-10"
                      data-testid="key-contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-2">Mobile Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <Input
                      type="tel"
                      placeholder="04XX XXX XXX"
                      value={formData.mobile}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      className="h-12 pl-10"
                      data-testid="key-contact-mobile"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Company Details */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#FEF3C7] flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-[#D97706]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#111111]">Company Details</h2>
                    <p className="text-[#64748B]">Your business information</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-2">Business Name *</label>
                  <Input
                    placeholder="ABC Solar Installations Pty Ltd"
                    value={formData.businessName}
                    onChange={(e) => handleChange('businessName', e.target.value)}
                    className="h-12"
                    data-testid="business-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-2">ABN *</label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <Input
                      placeholder="XX XXX XXX XXX"
                      value={formData.abn}
                      onChange={(e) => handleChange('abn', e.target.value)}
                      className="h-12 pl-10"
                      data-testid="business-abn"
                    />
                  </div>
                  <p className="text-sm text-[#64748B] mt-1">11-digit Australian Business Number</p>
                </div>

                {/* Accounts Contact */}
                <div className="border-t border-[#E2E8F0] pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-[#111111] mb-4">Accounts Contact</h3>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <Checkbox
                      id="accountsSameAsKey"
                      checked={formData.accountsSameAsKey}
                      onCheckedChange={(checked) => handleChange('accountsSameAsKey', checked)}
                      data-testid="accounts-same-as-key"
                    />
                    <label htmlFor="accountsSameAsKey" className="text-[#374151] cursor-pointer">
                      Same as key contact person
                    </label>
                  </div>

                  {!formData.accountsSameAsKey && (
                    <div className="space-y-4 bg-[#F8FAFC] rounded-lg p-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#374151] mb-2">First Name *</label>
                          <Input
                            placeholder="Jane"
                            value={formData.accountsFirstName}
                            onChange={(e) => handleChange('accountsFirstName', e.target.value)}
                            className="h-12"
                            data-testid="accounts-first-name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#374151] mb-2">Last Name *</label>
                          <Input
                            placeholder="Doe"
                            value={formData.accountsLastName}
                            onChange={(e) => handleChange('accountsLastName', e.target.value)}
                            className="h-12"
                            data-testid="accounts-last-name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#374151] mb-2">Email *</label>
                        <Input
                          type="email"
                          placeholder="accounts@company.com.au"
                          value={formData.accountsEmail}
                          onChange={(e) => handleChange('accountsEmail', e.target.value)}
                          className="h-12"
                          data-testid="accounts-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#374151] mb-2">Phone</label>
                        <Input
                          type="tel"
                          placeholder="04XX XXX XXX or (03) XXXX XXXX"
                          value={formData.accountsPhone}
                          onChange={(e) => handleChange('accountsPhone', e.target.value)}
                          className="h-12"
                          data-testid="accounts-phone"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Services */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#EDE9FE] flex items-center justify-center">
                    <Package className="w-6 h-6 text-[#7C3AED]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#111111]">Services</h2>
                    <p className="text-[#64748B]">Select the services you're interested in</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Packaging Collection */}
                  <div 
                    className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                      formData.interestedInCollection 
                        ? 'border-[#0D9488] bg-[#F0FDFA]' 
                        : 'border-[#E2E8F0] hover:border-[#0D9488]/50'
                    }`}
                    onClick={() => handleChange('interestedInCollection', !formData.interestedInCollection)}
                    data-testid="service-collection"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        formData.interestedInCollection ? 'bg-[#0D9488]' : 'bg-[#F0FDFA]'
                      }`}>
                        <Package className={`w-6 h-6 ${formData.interestedInCollection ? 'text-white' : 'text-[#0D9488]'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-[#111111]">Packaging Collection</h3>
                          <Checkbox
                            checked={formData.interestedInCollection}
                            onCheckedChange={(checked) => handleChange('interestedInCollection', checked)}
                          />
                        </div>
                        <p className="text-[#475569] mt-1">
                          We collect bulky packaging from your install sites — cardboard, foam, plastic wrapping from batteries, inverters, and electrical equipment.
                        </p>
                        <ul className="mt-3 space-y-1 text-sm text-[#64748B]">
                          <li>• Collection within 7 days (Standard) or 48 hours (Priority)</li>
                          <li>• Subscription includes up to 5 jobs per week</li>
                          <li>• No more weekend tip runs</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Delivery */}
                  <div 
                    className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                      formData.interestedInDelivery 
                        ? 'border-[#D97706] bg-[#FFFBEB]' 
                        : 'border-[#E2E8F0] hover:border-[#D97706]/50'
                    }`}
                    onClick={() => handleChange('interestedInDelivery', !formData.interestedInDelivery)}
                    data-testid="service-delivery"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        formData.interestedInDelivery ? 'bg-[#D97706]' : 'bg-[#FEF3C7]'
                      }`}>
                        <Truck className={`w-6 h-6 ${formData.interestedInDelivery ? 'text-white' : 'text-[#D97706]'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-[#111111]">Pickup & Delivery</h3>
                          <Checkbox
                            checked={formData.interestedInDelivery}
                            onCheckedChange={(checked) => handleChange('interestedInDelivery', checked)}
                          />
                        </div>
                        <p className="text-[#475569] mt-1">
                          Equipment logistics to support your installation workflow — pickup from suppliers and deliver to job sites.
                        </p>
                        <ul className="mt-3 space-y-1 text-sm text-[#64748B]">
                          <li>• Pickup equipment from suppliers or warehouses</li>
                          <li>• Deliver to job sites or customer locations</li>
                          <li>• Flexible scheduling around your install calendar</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Terms & Conditions */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#0D9488]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#111111]">Almost there!</h2>
                  <p className="text-[#64748B] mt-2">Please review our terms and conditions below</p>
                </div>

                {/* Scroll reminder notification */}
                {showScrollReminder && (
                  <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-lg p-4 text-center animate-pulse">
                    <p className="text-[#92400E]">
                      Please scroll through and read the terms below before submitting
                    </p>
                  </div>
                )}

                {/* Single scrollable terms container */}
                <div 
                  className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white"
                  style={{ maxHeight: '400px' }}
                >
                  <div 
                    className="p-6 overflow-y-auto text-sm text-[#475569] space-y-6"
                    style={{ maxHeight: '400px' }}
                    onScroll={handleTermsScroll}
                    data-testid="terms-scroll-container"
                  >
                    {/* Part 1: General Terms */}
                    <div>
                      <h3 className="text-lg font-bold text-[#0D9488] mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-[#0D9488] text-white rounded-full flex items-center justify-center text-xs">1</span>
                        General Terms & Conditions
                      </h3>
                      
                      <div className="space-y-3">
                        <p><strong>About Transa:</strong> "Transa" refers to The Trustee for Keystone Heritage Trust trading as Transa (ABN 83 164 418 663), providing installer-focused site logistics services in Metropolitan Melbourne.</p>
                        <p><strong>Acceptance:</strong> By subscribing to Transa services, you agree to be bound by these Terms & Conditions.</p>
                        <p><strong>Scope:</strong> Transa provides logistics and reverse-logistics services including collection of installation packaging. We do not provide waste processing, demolition, or hazardous-waste services.</p>
                        <p><strong>Service Area:</strong> Services are available only within Metropolitan Melbourne LGAs unless otherwise agreed.</p>
                        <p><strong>Booking:</strong> All bookings are requests subject to confirmation. Pickup timeframes depend on the selected service model and may be affected by access constraints, weather, or safety considerations.</p>
                        <p><strong>Customer Responsibilities:</strong> You must ensure materials presented match the booking description, comply with all applicable laws, and ensure materials are safe to handle.</p>
                        <p><strong>Fees & Payment:</strong> Pricing depends on service model, volume, urgency, and location. Invoices are payable within the stated payment period. Late payments may incur administrative fees.</p>
                        <p><strong>Liability:</strong> To the maximum extent permitted by law, Transa is not liable for indirect, incidental, or consequential losses. Nothing excludes rights under Australian Consumer Law.</p>
                        <p><strong>Governing Law:</strong> These Terms are governed by the laws of Victoria, Australia.</p>
                        <p><strong>Amendments:</strong> Transa may update these terms from time to time. Continued use of services constitutes acceptance of updated terms.</p>
                        <p><strong>Termination:</strong> Either party may terminate the subscription with reasonable notice. Outstanding fees remain payable.</p>
                      </div>
                    </div>

                    <hr className="border-[#E2E8F0]" />

                    {/* Part 2: Service Terms */}
                    <div>
                      <h3 className="text-lg font-bold text-[#0D9488] mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-[#0D9488] text-white rounded-full flex items-center justify-center text-xs">2</span>
                        Service Terms
                      </h3>
                      
                      <div className="space-y-3">
                        <p><strong>Standard Pickup:</strong> Pay-per-job pickup with collection within 7 days of confirmed booking. No guaranteed pickup day or time within the window.</p>
                        <p><strong>Subscription Service:</strong> Discounted pricing for regular installers. Typically includes up to 5 jobs per week. Excess jobs may be charged per job or redirected to Priority service.</p>
                        <p><strong>Priority Pickup (48 Hours):</strong> Pickup targeted within 48 hours of booking confirmation. Cancellations within 24 hours may be fully chargeable.</p>
                        <p><strong>Billing:</strong> Subscription billed weekly or monthly as agreed. Minimum subscription term may apply.</p>
                        <p><strong>Cancellations:</strong> Bookings cancelled within 24 hours of scheduled pickup may incur a fee.</p>
                      </div>
                    </div>

                    <hr className="border-[#E2E8F0]" />

                    {/* Part 3: Battery Installation Service Conditions */}
                    <div>
                      <h3 className="text-lg font-bold text-[#7C3AED] mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-[#7C3AED] text-white rounded-full flex items-center justify-center text-xs">3</span>
                        Battery Installation Service Conditions
                      </h3>
                      
                      <div className="bg-[#EDE9FE]/50 rounded-lg p-4 space-y-3">
                        <p><strong>Purpose:</strong> This service supports residential battery installations. Transa is a <em>rubbish collection</em> service, not a rubbish sorting service.</p>
                        
                        <p><strong>Scope:</strong> One (1) residential battery installation per booking only. All waste must originate from the same site.</p>
                        
                        <div>
                          <p className="font-semibold text-[#111111]">Required Waste Sorting (Mandatory):</p>
                          <ul className="list-disc pl-5 mt-1 space-y-1">
                            <li>Recyclable cardboard — Stacked neatly together</li>
                            <li>Large packaging — Foam, polystyrene grouped together</li>
                            <li>Small loose waste — Cable off-cuts, ties, plastics in one bag</li>
                          </ul>
                        </div>
                        
                        <p><strong>Time Allowance:</strong> 10 minutes on-site collection time included per booking.</p>
                        
                        <p className="text-[#DC2626]"><strong>Additional Charges:</strong> If sorting or cleanup is required: <strong>$20 per additional 10 minutes</strong></p>
                        
                        <div>
                          <p className="font-semibold text-[#DC2626]">Prohibited Materials:</p>
                          <ul className="list-disc pl-5 mt-1 space-y-1 text-[#DC2626]">
                            <li>Hazardous or flammable materials</li>
                            <li>Fuel, oils, or chemicals</li>
                            <li>Gas cylinders or pressurised containers</li>
                            <li>Asbestos or asbestos-containing materials</li>
                          </ul>
                        </div>
                        
                        <p><strong>Right to Refuse:</strong> Transa may refuse collection, apply additional charges, or cancel bookings if these conditions are not met.</p>
                      </div>
                    </div>

                    <hr className="border-[#E2E8F0]" />

                    {/* End section */}
                    <div className="text-center py-4">
                      <p className="text-[#64748B]">By registering, you also acknowledge our <Link to="/privacy" target="_blank" className="text-[#0D9488] hover:underline">Privacy Policy</Link>.</p>
                      <p className="text-[#0D9488] font-medium mt-4">— End of Terms —</p>
                      <Link to="/terms" target="_blank" className="text-sm text-[#64748B] hover:underline">View full terms on website →</Link>
                    </div>
                  </div>
                </div>

                {/* Checkbox */}
                <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                  <Checkbox
                    id="agreedTerms"
                    checked={formData.agreedTerms}
                    onCheckedChange={(checked) => handleChange('agreedTerms', checked)}
                    data-testid="agree-terms"
                  />
                  <label htmlFor="agreedTerms" className="text-sm text-[#374151] cursor-pointer">
                    I have read and agree to the Terms & Conditions and Service Conditions above
                  </label>
                </div>

                {/* Progress indicator */}
                {hasReadTerms && !isSubmitting && (
                  <div className="text-center text-sm text-[#10B981]">
                    ✓ You've reviewed all the terms
                  </div>
                )}

                {/* Submission Progress Overlay */}
                {isSubmitting && (
                  <div className="bg-gradient-to-br from-[#F0FDFA] to-white rounded-xl p-6 border border-[#0D9488]/20">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 rounded-full bg-[#0D9488] flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <Loader2 className="w-8 h-8 text-white animate-spin" />
                      </div>
                      <h3 className="text-xl font-bold text-[#111111]">
                        {SUBMISSION_STEPS[submissionStep]?.label || 'Processing...'}
                      </h3>
                      <p className="text-[#64748B] mt-1">Please wait while we process your registration</p>
                    </div>
                    
                    {/* Progress Steps */}
                    <div className="space-y-3 max-w-sm mx-auto">
                      {SUBMISSION_STEPS.map((s, idx) => (
                        <div 
                          key={s.id}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                            idx < submissionStep 
                              ? 'bg-[#D1FAE5] text-[#059669]' 
                              : idx === submissionStep 
                                ? 'bg-[#0D9488] text-white' 
                                : 'bg-[#F1F5F9] text-[#94A3B8]'
                          }`}
                        >
                          <span className="text-lg">{idx < submissionStep ? '✓' : s.icon}</span>
                          <span className={`text-sm font-medium ${idx === submissionStep ? 'animate-pulse' : ''}`}>
                            {s.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Navigation Buttons - Hidden during submission */}
            {!isSubmitting && (
              <div className="flex justify-between mt-8 pt-6 border-t border-[#E2E8F0]">
                {step > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="border-[#E2E8F0] text-[#374151]"
                    data-testid="prev-step-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                ) : (
                  <Link to="/">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-[#E2E8F0] text-[#374151]"
                    >
                      Cancel
                    </Button>
                  </Link>
                )}

                {step < 4 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                    data-testid="next-step-btn"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmitClick}
                    disabled={isSubmitting || !hasReadTerms || !formData.agreedTerms}
                    className="bg-[#0D9488] hover:bg-[#0F766E] text-white min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-btn"
                  >
                    Submit Application
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Text */}
        <p className="text-center text-[#64748B] mt-6">
          Already a subscriber? <Link to="/manage" className="text-[#0D9488] hover:underline">Sign in to your portal</Link>
        </p>
      </div>
    </main>
  );
};

export default SubscriptionSignupPage;
